import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { FadeUp, Eyebrow } from "./Reveal";
import { detectCurrency } from "@/lib/currency";
import { PRICE_DISPLAY } from "./Pricing";

const ROWS = [
  { label: "Workout durations", fit: "5 (10/20/30/40/50 min)", other: "Usually 1-2 fixed lengths" },
  { label: "Exercise library", fit: "500+ movements", other: "Typically 100-200" },
  { label: "Demonstrators", fit: "Male + Female", other: "Usually one body type" },
  { label: "Smartwatch coaching", fit: "Live, real-time", other: "Log-only or post-session" },
  { label: "Meal recipes", fit: "Built-in, high-protein", other: "Separate subscription" },
  { label: "Circuit training", fit: "All durations", other: "Limited or premium-only" },
  { label: "Offline access", fit: "All sessions", other: "Often streaming-only" },
  { label: "Pricing", fit: "__PRICE__", other: "$20-40+/month typical" },
];

export const Comparison = () => {
  const [currency, setCurrency] = useState("NZD");

  useEffect(() => {
    detectCurrency().then(setCurrency);
  }, []);

  const priceText = `From ${PRICE_DISPLAY.monthly[currency]}/mo ${currency}`;

  return (
  <section id="compare" data-testid="comparison-section" className="relative py-32 bg-carbon/40 scroll-mt-20">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <FadeUp className="text-center">
        <Eyebrow>What Makes Fit50 Different</Eyebrow>
        <h2 className="font-display font-extrabold uppercase tracking-tight leading-tight text-3xl sm:text-4xl lg:text-5xl mt-4">
          Most apps charge you more for less. <span className="text-volt">Fit50 is the opposite.</span>
        </h2>
        <p className="mt-5 max-w-2xl mx-auto text-base sm:text-lg text-neutral-400 leading-relaxed">
          We built the library, the nutrition, the smartwatch integration, and the adaptive
          engine that other apps charge separately for — and then compressed the whole thing
          to work in 10 minutes.
        </p>
      </FadeUp>

      <FadeUp delay={0.15}>
        <div className="mt-14 border border-white/10 bg-obsidian overflow-hidden">
          <div className="overflow-x-auto">
            <table data-testid="comparison-table" className="w-full min-w-[620px] text-left">
              <thead>
                <tr className="border-b border-white/10 bg-volt">
                  <th className="p-5 sm:p-6 font-mono text-[10px] tracking-[0.25em] uppercase text-white w-2/5">
                    Compare
                  </th>
                  <th className="p-5 sm:p-6 font-display font-black uppercase tracking-wide text-lg sm:text-xl text-white">
                    Fit50
                  </th>
                  <th className="p-5 sm:p-6 font-display font-bold uppercase tracking-wide text-lg sm:text-xl text-white">
                    Other Fitness Apps
                  </th>
                </tr>
              </thead>
              <tbody>
                {ROWS.map((r) => (
                  <tr
                    key={r.label}
                    data-testid={`comparison-row-${r.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition-colors duration-200"
                  >
                    <td className="p-5 sm:p-6 font-mono text-xs tracking-widest uppercase text-neutral-400">
                      {r.label}
                    </td>
                    <td className="p-5 sm:p-6 bg-volt/[0.06]">
                      <span className="inline-flex items-center gap-2 text-sm sm:text-base font-semibold text-volt">
                        <Check className="h-4 w-4 shrink-0" strokeWidth={3} />
                        {r.fit === "__PRICE__" ? priceText : r.fit}
                      </span>
                    </td>
                    <td className="p-5 sm:p-6 text-sm sm:text-base text-neutral-500">
                      {r.other}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </FadeUp>
    </div>
  </section>
  );
};
